import React, { useState } from "react";
import { Check, ChevronDown, FolderOpen, Loader2 } from "lucide-react";
import ProjectRow from "./ProjectRow";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

const FILTERS = [
  ["all", "All", () => true],
  ["published", "Published", (p) => !!p.published_version_id],
  ["drafts", "Drafts", (p) => !p.published_version_id],
];

const FilterMenu = ({ value, onChange, projects }) => {
  const count = (fn) => projects.filter(fn).length;
  const cur = FILTERS.find((f) => f[0] === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="projects-filter" className="h-10 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-[15px] text-slate-700 flex items-center gap-2 transition-colors">
          {cur[1]} ({count(cur[2])}) <ChevronDown size={16} className="text-slate-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44 rounded-xl bg-white border-slate-200 p-1">
        {FILTERS.map(([id, label, fn]) => (
          <DropdownMenuItem key={id} onSelect={() => onChange(id)} data-testid={`projects-filter-${id}`} className="justify-between rounded-lg cursor-pointer">
            {label} ({count(fn)}) {id === value && <Check size={14} className="text-sky-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ContinueWorking = ({ projects, onDelete, onDuplicate, onRename, error, onRetry, retrying }) => {
  const [filter, setFilter] = useState("all");
  const shown = (projects || []).filter(FILTERS.find((f) => f[0] === filter)[2]);
  return (
    <section className="mt-16 sm:mt-24" data-testid="continue-working">
      <div className="flex items-center justify-between gap-4 mb-2">
        <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-slate-900">Continue working</h2>
        {projects?.length > 0 && <FilterMenu value={filter} onChange={setFilter} projects={projects} />}
      </div>
      {projects === null ? (
        <div className="space-y-4 pt-4">{[0, 1, 2].map((i) => <div key={i} className="h-28 rounded-2xl shimmer" />)}</div>
      ) : error && projects.length === 0 ? (
        <div className="mt-4 rounded-3xl border border-dashed border-amber-300 py-14 text-center bg-amber-50/60" data-testid="projects-load-error">
          <p className="text-sm text-slate-600">Couldn't load your projects — the server may be waking up.</p>
          <button onClick={onRetry} disabled={retrying} data-testid="projects-retry-btn" className="mt-4 h-10 px-5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:opacity-60 inline-flex items-center gap-2">{retrying && <Loader2 size={14} className="animate-spin" />}{retrying ? "Trying…" : "Try again"}</button>
        </div>
      ) : shown.length === 0 ? (
        <div className="mt-4 rounded-3xl border border-dashed border-slate-300 py-14 text-center bg-white/60" data-testid="projects-empty">
          <FolderOpen className="mx-auto text-slate-400" />
          <p className="mt-3 text-sm text-slate-500">{projects.length ? "Nothing here yet." : "No projects yet — describe your first app above."}</p>
        </div>
      ) : (
        <div data-testid="projects-grid">
          {shown.map((p) => <ProjectRow key={p.id} project={p} onDelete={onDelete} onDuplicate={onDuplicate} onRename={onRename} />)}
        </div>
      )}
    </section>
  );
};

export default ContinueWorking;
